import { useEffect, useState } from "react";
import { API } from "../../../services/api";
import { emptyDeliveryForm, sortDeliveryPlannings } from "./deliveryCalendar";
import {
    deliveryPayload,
    fullDeliveryTarget,
    planningToForm,
    withFrequencyDays,
    withPlanningMode,
    withToggledTarget,
} from "./deliveryPlanningForm";

// Livraisons planifiées, EHPAD et livreurs, chargés à l'ouverture de l'onglet.
export function useDeliveryData() {
    const [plannings, setPlannings] = useState([]);
    const [ehpads, setEhpads] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const [planningsRes, ehpadsRes, driversRes] = await Promise.all([
                    API.get("/admin/delivery-plannings"),
                    API.get("/admin/ehpads"),
                    API.get("/admin/delivery-plannings/drivers"),
                ]);
                setPlannings(sortDeliveryPlannings(planningsRes.data));
                setEhpads(ehpadsRes.data);
                setDrivers(driversRes.data);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    return { plannings, setPlannings, ehpads, drivers, loading };
}

// Modale de création ou d'édition d'une livraison : formulaire, sélection des EHPAD, enregistrement et suppression.
export function useDeliveryForm({ ehpads, setPlannings }) {
    const [form, setForm] = useState(emptyDeliveryForm);
    const [editingId, setEditingId] = useState(null);
    const [modalKind, setModalKind] = useState("regular");
    const [modalOpen, setModalOpen] = useState(false);
    const [ehpadSearch, setEhpadSearch] = useState("");
    const [hoveredEhpadId, setHoveredEhpadId] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState(null);

    const resetForm = () => {
        setForm(emptyDeliveryForm);
        setEditingId(null);
        setModalKind("regular");
        setModalOpen(false);
        setFormError(null);
    };

    const openModal = (nextForm, id, kind) => {
        setForm(nextForm);
        setEditingId(id);
        setModalKind(kind);
        setEhpadSearch("");
        setHoveredEhpadId(null);
        setFormError(null);
        setModalOpen(true);
    };

    const openCreateModal = (firstDeliveryDate = "", kind = "regular") => {
        const exceptional = kind === "exceptional";
        openModal({
            ...emptyDeliveryForm,
            firstDeliveryDate,
            frequency: exceptional ? "PONCTUELLE" : "HEBDOMADAIRE",
            frequencyDays: exceptional ? 7 : emptyDeliveryForm.frequencyDays,
        }, null, kind);
    };

    const handleEdit = (planning) => {
        openModal(planningToForm(planning), planning.id, planning.frequency === "PONCTUELLE" ? "exceptional" : "regular");
    };

    const setPlanningMode = (mode) => {
        setModalKind(mode === "PONCTUELLE" ? "exceptional" : "regular");
        setForm(current => withPlanningMode(current, mode));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError(null);
        if (form.targets.length === 0) {
            setFormError("Ajoutez au moins un EHPAD à la livraison.");
            return;
        }

        setSubmitting(true);
        const payload = deliveryPayload(form);
        try {
            if (editingId) {
                const { data } = await API.put(`/admin/delivery-plannings/${editingId}`, payload);
                setPlannings(prev => sortDeliveryPlannings(prev.map(item => item.id === editingId ? data : item)));
            } else {
                const { data } = await API.post("/admin/delivery-plannings", payload);
                setPlannings(prev => sortDeliveryPlannings([...prev, data]));
            }
            resetForm();
        } catch (err) {
            setFormError(err.response?.data?.message ?? "Erreur lors de l'enregistrement de la livraison.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if (!globalThis.confirm("Supprimer cette livraison du planning ?")) return;
        try {
            await API.delete(`/admin/delivery-plannings/${id}`);
            setPlannings(prev => prev.filter(item => item.id !== id));
            if (editingId === id) resetForm();
        } catch {
            alert("Erreur lors de la suppression.");
        }
    };

    return {
        form, setForm, editingId, modalKind, modalOpen, ehpadSearch, setEhpadSearch, hoveredEhpadId, setHoveredEhpadId,
        submitting, formError, resetForm, openCreateModal, handleEdit, setPlanningMode, handleSubmit, handleDelete,
        toggleDeliveryTarget: target => setForm(current => withToggledTarget(current, target)),
        selectAllDeliveryTargets: () => setForm(current => ({ ...current, targets: ehpads.map(ehpad => fullDeliveryTarget(ehpad)) })),
        clearDeliveryTargets: () => setForm(current => ({ ...current, targets: [] })),
        handleFrequencyDaysChange: value => setForm(current => withFrequencyDays(current, value)),
    };
}

// Glisser-déposer d'une occurrence vers un autre jour : seule cette occurrence est déplacée.
export function useDeliveryDragDrop(setPlannings) {
    const [movingOccurrenceKey, setMovingOccurrenceKey] = useState(null);

    const handleDeliveryDragStart = (event, planning) => {
        const payload = JSON.stringify({ planningId: planning.id, originalDate: planning.occurrenceDate });
        event.dataTransfer.setData("application/json", payload);
        event.dataTransfer.effectAllowed = "move";
        setMovingOccurrenceKey(`${planning.id}-${planning.occurrenceDate}`);
    };

    const handleDeliveryDragEnd = () => {
        setMovingOccurrenceKey(null);
    };

    const handleDeliveryDrop = async (event, dateKey, canAddOnDay) => {
        event.preventDefault();
        if (!canAddOnDay) return;
        let payload;
        try {
            payload = JSON.parse(event.dataTransfer.getData("application/json"));
        } catch {
            return;
        }
        if (!payload?.planningId || !payload?.originalDate || payload.originalDate === dateKey) {
            setMovingOccurrenceKey(null);
            return;
        }
        setMovingOccurrenceKey(`${payload.planningId}-${payload.originalDate}`);
        try {
            const { data } = await API.patch(`/admin/delivery-plannings/${payload.planningId}/occurrences`, {
                originalDate: payload.originalDate,
                movedDate: dateKey,
            });
            setPlannings(prev => sortDeliveryPlannings(prev.map(item => item.id === data.id ? data : item)));
        } catch {
            alert("Erreur lors du déplacement de la livraison.");
        } finally {
            setMovingOccurrenceKey(null);
        }
    };

    return { movingOccurrenceKey, handleDeliveryDragStart, handleDeliveryDragEnd, handleDeliveryDrop };
}
